// src/pages/admin/AdminReviewDetail.tsx
//
// Reviewing one piece of work (/admin/reviews/:id).
//
// Left: what the lesson asked for, the child's evidence and reflection,
// earlier tries and their feedback. Right: the feedback form — what went
// well, one thing to improve, the next step, rubric scores 1–3, and an
// optional voice note (the way to reach a 6–9-year-old who can't read
// long comments yet). The comment bank offers sentences tutors used on
// this lesson before.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  RUBRIC_LABELS,
  SCORE_LABELS,
  errorText,
  putFile,
  useGetCommentBankQuery,
  useGetReviewMediaUrlMutation,
  useGetReviewQueueQuery,
  useGetSubmissionQuery,
  useGetVoiceUploadUrlMutation,
  useSendReviewMutation,
} from "../../app/api/learningApi";
import type { Evidence, Scores } from "../../app/api/learningApi";
import { Button, EmptyState, Skeleton } from "./AdminShared";
import MediaPlayer from "../../components/MediaPlayer";
import { ProjectViewer } from "../../components/workspace";

import { ArrowLeft, ArrowRight, Blocks, Check, FileText, Link2, Mic, RotateCcw, Sparkles, Sprout } from "lucide-react";
const box = "rounded-[20px] border border-[#e5e8ef] bg-white p-5";
const label = "mb-1.5 block text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]";
const input =
  "w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2.5 text-sm text-[#16213e] outline-none focus:border-[#1b2d5e]";

/* ─── evidence ───────────────────────────────────────────────── */

const KIND_TEXT: Record<string, string> = {
  scratch: "Scratch project",
  web: "Code project (HTML, CSS, JavaScript)",
  react: "React app (runs in the browser)",
  native: "React Native app (phone preview)",
};

/**
 * A project made in the in-app workspace (Scratch or the code editor): the
 * copy saved when the child handed in. The tutor can run it, look inside
 * and download it; nothing they change is saved.
 */
function ProjectItem({ id, ev, childName }: { id: string; ev: Evidence; childName?: string }) {
  const [open, setOpen] = useState(false);
  const [getUrl] = useGetReviewMediaUrlMutation();
  const kind = ev.projectKind ?? "scratch";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#e5e8ef] bg-[#f6f8fc] p-3">
      <Blocks className="h-5 w-5 text-[#1b2d5e]" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-[#16213e]">{ev.fileName ?? "Project"}</span>
        <span className="block text-xs text-[#5f6780]">
          {KIND_TEXT[kind]}, made in the lesson's workspace
        </span>
      </span>
      <Button size="sm" onClick={() => setOpen(true)}>
        Open {kind === "scratch" ? "in Scratch" : kind === "native" ? "the app" : "the code"}
      </Button>
      {open && ev.key && (
        <ProjectViewer
          kind={kind}
          name={ev.fileName ?? "Project"}
          subtitle={childName ? `${childName}'s work, as handed in` : "As handed in"}
          getUrl={async () => (await getUrl({ id, key: ev.key! }).unwrap()).data.url}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function EvidenceItem({ id, ev, childName }: { id: string; ev: Evidence; childName?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [getUrl, { isLoading, error }] = useGetReviewMediaUrlMutation();

  useEffect(() => {
    if (ev.key && (ev.type === "photo" || ev.type === "audio")) {
      getUrl({ id, key: ev.key }).unwrap().then((r) => setUrl(r.data.url)).catch(() => {});
    }
  }, [ev.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (ev.type === "text")
    return <p className="whitespace-pre-wrap rounded-xl bg-[#f9fafc] p-3 text-sm leading-6 text-[#16213e]">{ev.text}</p>;
  if (ev.type === "link")
    return (
      <a href={ev.url ?? "#"} target="_blank" rel="noreferrer noopener" className="block break-all rounded-xl bg-[#f9fafc] p-3 text-sm font-semibold text-[#1b2d5e]">
        <Link2 className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{ev.url}
      </a>
    );
  if (ev.type === "project") return <ProjectItem id={id} ev={ev} childName={childName} />;
  if (!ev.key) return null;
  if (error) return <p className="text-xs text-[#b94a3a]">{errorText(error, "Could not open the file")}</p>;
  if (!url)
    return (
      <button
        onClick={() => getUrl({ id, key: ev.key! }).unwrap().then((r) => setUrl(r.data.url)).catch(() => {})}
        className="rounded-xl bg-[#f1f3f8] px-3 py-2 text-xs font-semibold text-[#4a44b0]"
      >
        {isLoading ? "Opening…" : `Open ${ev.type}${ev.fileName ? `: ${ev.fileName}` : ""}`}
      </button>
    );
  if (ev.type === "photo")
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={ev.fileName ?? "evidence"} className="max-h-96 rounded-xl border border-[#e5e8ef]" />
      </a>
    );
  if (ev.type === "video") return <MediaPlayer src={url} />;
  if (ev.type === "audio") return <MediaPlayer src={url} kind="audio" />;
  return <a href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#1b2d5e]">Download {ev.fileName}</a>;
}

/* ─── voice note recorder ────────────────────────────────────── */

function VoiceRecorder({ onRecorded, disabled }: { onRecorded: (blob: Blob | null) => void; disabled: boolean }) {
  const [state, setState] = useState<"idle" | "recording" | "done">("idle");
  const [seconds, setSeconds] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
    rec.current?.stream.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
        setPreview(URL.createObjectURL(blob));
        setState("done");
        onRecorded(blob);
      };
      mr.start();
      rec.current = mr;
      setSeconds(0);
      setState("recording");
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      setErr("Microphone not available — allow microphone access in the browser.");
    }
  };

  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    if (rec.current?.state === "recording") rec.current.stop();
  };

  // 3-minute cap
  useEffect(() => {
    if (state === "recording" && seconds >= 180) stop();
  }, [seconds]); // eslint-disable-line react-hooks/exhaustive-deps

  const discard = () => {
    setPreview(null);
    setState("idle");
    onRecorded(null);
  };

  return (
    <div className="rounded-xl border border-dashed border-[#c9cfe0] p-3">
      {state === "idle" && (
        <Button size="sm" variant="secondary" onClick={start} disabled={disabled}><Mic className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Record a voice note</Button>
      )}
      {state === "recording" && (
        <div className="flex items-center gap-3">
          <span className="h-3 w-3 animate-pulse rounded-full bg-[#e5484d]" />
          <span className="text-sm font-bold text-[#16213e]">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</span>
          <Button size="sm" onClick={stop}>Stop</Button>
        </div>
      )}
      {state === "done" && preview && (
        <div className="space-y-2">
          <MediaPlayer src={preview} kind="audio" />
          <button onClick={discard} className="text-xs font-semibold text-[#b94a3a]">Discard and record again</button>
        </div>
      )}
      {err && <p className="mt-2 text-xs text-[#b94a3a]">{err}</p>}
      <p className="mt-2 text-[11.5px] text-[#8189a0]">Speak to the child by name. Up to 3 minutes. Best for ages 6–9.</p>
    </div>
  );
}

/* ═══ PAGE ═══════════════════════════════════════════════════════ */

export default function AdminReviewDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetSubmissionQuery(id);
  const sub = data?.data;
  const { data: bank } = useGetCommentBankQuery(sub?.lesson?.key ?? "", { skip: !sub?.lesson?.key });
  const { data: queue } = useGetReviewQueueQuery({ status: "submitted" });
  const [getVoiceUrl] = useGetVoiceUploadUrlMutation();
  const [sendReview, { isLoading: sending }] = useSendReviewMutation();

  const [glow, setGlow] = useState("");
  const [grow, setGrow] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [scores, setScores] = useState<Scores>({});
  const [voice, setVoice] = useState<Blob | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setGlow(""); setGrow(""); setNextStep(""); setScores({}); setVoice(null); setMsg(null);
  }, [id]);

  if (isLoading) return <div className="space-y-3"><Skeleton className="h-24 rounded-[20px]" /><Skeleton className="h-96 rounded-[20px]" /></div>;
  if (error || !sub)
    return <EmptyState icon={FileText} title="Not found" action={<Link to="/admin/reviews" className="text-sm font-semibold text-[#1b2d5e]"><ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Reviews</Link>} />;

  const last = sub.attempts[sub.attempts.length - 1];
  const earlier = sub.attempts.slice(0, -1);
  const canReview = sub.status === "submitted";
  const focus = sub.lesson?.rubricFocus ?? [];
  const areas = [...focus, ...Object.keys(RUBRIC_LABELS).filter((a) => !focus.includes(a))];
  const nextId = queue?.data.find((q) => q.id !== sub.id)?.id;

  const submit = async (decision: "accepted" | "needs_revision") => {
    setMsg(null);
    try {
      let voiceNoteKey: string | null = null;
      if (voice) {
        const type = voice.type || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const { data: up } = await getVoiceUrl({ id: sub.id, fileName: `voice-note.${ext}`, contentType: type }).unwrap();
        await putFile(up.uploadUrl, voice, type);
        voiceNoteKey = up.key;
      }
      await sendReview({ id: sub.id, decision, glow, grow, nextStep, scores, voiceNoteKey }).unwrap();
      setMsg({ ok: true, text: decision === "accepted" ? "Accepted — feedback sent." : "Sent back with feedback." });
      if (nextId) navigate(`/admin/reviews/${nextId}`);
    } catch (err) {
      setMsg({ ok: false, text: errorText(err, "Could not send the feedback") });
    }
  };

  const Bank = ({ items, onPick }: { items?: string[]; onPick: (s: string) => void }) =>
    items && items.length > 0 ? (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {items.slice(0, 4).map((s, i) => (
          <button key={i} title={s} onClick={() => onPick(s)} className="max-w-full truncate rounded-full bg-[#f1f3f8] px-2.5 py-1 text-[11.5px] text-[#4a44b0] hover:bg-[#e5e8ef]">
            {s.length > 60 ? `${s.slice(0, 60)}…` : s}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <Link to="/admin/reviews" className="text-xs font-semibold text-[#1b2d5e]"><ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Reviews</Link>
        {nextId && <Link to={`/admin/reviews/${nextId}`} className="text-xs font-semibold text-[#1b2d5e]">Skip to next{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></Link>}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        {/* ── The work ── */}
        <div className="space-y-4">
          <div className={box}>
            <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#1b2d5e]">
              {sub.child ? `${sub.child.firstName} ${sub.child.lastInitial}. · age ${sub.child.age}` : "Child"}
              {sub.week && ` · ${sub.week.term} week ${sub.week.weekNumber}`}
            </p>
            <h1 className="mt-1 text-xl font-bold tracking-[-0.02em] text-[#16213e]">{sub.lesson?.title}</h1>
            {sub.lesson?.objective && <p className="mt-1 text-sm text-[#5f6780]">{sub.lesson.objective}</p>}
            {sub.lesson?.evidencePrompt && (
              <p className="mt-3 rounded-xl bg-[#f1f3f8] px-3 py-2 text-xs text-[#4a44b0]"><b>Asked for:</b> {sub.lesson.evidencePrompt}</p>
            )}
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold text-[#8189a0]">The activity steps</summary>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs leading-5 text-[#3b4560]">
                {sub.lesson?.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </details>
          </div>

          {last && (
            <div className={box}>
              <p className={label}>
                Try {last.number} · handed in {new Date(last.submittedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                {sub.watchedAt ? " · watched the lesson" : " · did not open the video"}
              </p>
              {!sub.storageConfigured && last.evidence.some((e) => e.key) && (
                <p className="mb-2 text-xs text-[#7a4a10]">File storage is not set up, so files can't be opened here.</p>
              )}
              <div className="space-y-3">
                {last.evidence.map((e, i) => <EvidenceItem key={i} id={sub.id} ev={e} childName={sub.child?.firstName} />)}
              </div>
              {(last.reflection.built || last.reflection.helps || last.reflection.learned) && (
                <div className="mt-4 space-y-1.5 rounded-xl border border-[#e5e8ef] p-3 text-sm text-[#16213e]">
                  {last.reflection.built && <p><b>What I built:</b> {last.reflection.built}</p>}
                  {last.reflection.helps && <p><b>Who it helps:</b> {last.reflection.helps}</p>}
                  {last.reflection.learned && <p><b>What I learned:</b> {last.reflection.learned}</p>}
                </div>
              )}
            </div>
          )}

          {earlier.length > 0 && (
            <details className={box}>
              <summary className="cursor-pointer text-sm font-semibold text-[#3b4560]">Earlier tries ({earlier.length})</summary>
              <div className="mt-3 space-y-4">
                {earlier.map((a) => (
                  <div key={a.id} className="border-t border-[#ebeff7] pt-3">
                    <p className={label}>Try {a.number}</p>
                    <div className="space-y-2">{a.evidence.map((e, i) => <EvidenceItem key={i} id={sub.id} ev={e} childName={sub.child?.firstName} />)}</div>
                    {a.review && (
                      <p className="mt-2 text-xs leading-5 text-[#5f6780]">
                        <b>{a.review.reviewerName}:</b> {a.review.grow ?? ""} {a.review.nextStep ? `Next: ${a.review.nextStep}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          {sub.recentWork.length > 0 && (
            <div className={box}>
              <p className={label}>{sub.child?.firstName}'s recent work</p>
              <ul className="space-y-1 text-xs text-[#3b4560]">
                {sub.recentWork.map((w) => (
                  <li key={w.lessonKey}>{w.lessonTitle} — {w.status.replace("_", " ")} ({w.attempts} {w.attempts === 1 ? "try" : "tries"})</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── Feedback ── */}
        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          {!canReview ? (
            <div className={box}>
              <p className="text-sm font-bold text-[#16213e]">
                {sub.status === "accepted" ? "Accepted" : sub.status === "needs_revision" ? "Sent back — waiting for the next try" : "Not handed in yet"}
              </p>
              {last?.review && (
                <div className="mt-3 space-y-1.5 text-sm text-[#3b4560]">
                  {last.review.glow && <p><Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{last.review.glow}</p>}
                  {last.review.grow && <p><Sprout className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{last.review.grow}</p>}
                  {last.review.nextStep && <p><ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{last.review.nextStep}</p>}
                  <p className="text-xs text-[#8189a0]">— {last.review.reviewerName}</p>
                </div>
              )}
            </div>
          ) : (
            <div className={box}>
              <p className="mb-3 text-base font-bold text-[#16213e]">Your feedback</p>

              <label className={label}><Sparkles className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}What went well</label>
              <textarea className={`${input} min-h-[70px]`} value={glow} onChange={(e) => setGlow(e.target.value)} placeholder={`Be specific: what exactly did ${sub.child?.firstName ?? "they"} do well?`} />
              <Bank items={bank?.data.glow} onPick={setGlow} />

              <label className={`${label} mt-3`}><Sprout className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}One thing to improve</label>
              <textarea className={`${input} min-h-[60px]`} value={grow} onChange={(e) => setGrow(e.target.value)} placeholder="Just one thing — the most important one" />
              <Bank items={bank?.data.grow} onPick={setGrow} />

              <label className={`${label} mt-3`}><ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Next step</label>
              <textarea className={`${input} min-h-[60px]`} value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="A concrete action they can take" />
              <Bank items={bank?.data.nextStep} onPick={setNextStep} />

              <label className={`${label} mt-4`}>Scores (1 Emerging · 2 Developing · 3 Strong)</label>
              <div className="space-y-1.5">
                {areas.map((a) => (
                  <div key={a} className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${focus.includes(a) ? "font-bold text-[#16213e]" : "text-[#8189a0]"}`}>
                      {RUBRIC_LABELS[a]}{focus.includes(a) && " ·"}
                    </span>
                    <div className="flex gap-1">
                      {[1, 2, 3].map((n) => (
                        <button
                          key={n}
                          title={SCORE_LABELS[n]}
                          onClick={() => setScores((s) => ({ ...s, [a]: s[a] === n ? undefined : n }))}
                          className={`h-7 w-7 rounded-lg text-xs font-bold ${scores[a] === n ? "bg-[#1b2d5e] text-white" : "bg-[#f1f3f8] text-[#8189a0]"}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-[11.5px] text-[#a6adbf]">Bold areas are the ones this lesson focuses on.</p>

              <label className={`${label} mt-4`}>Voice note</label>
              <VoiceRecorder onRecorded={setVoice} disabled={!sub.storageConfigured} />

              {msg && <p className={`mt-3 text-sm font-semibold ${msg.ok ? "text-[#2a7a56]" : "text-[#b94a3a]"}`}>{msg.text}</p>}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button variant="secondary" onClick={() => submit("needs_revision")} disabled={sending}><RotateCcw className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Try again</Button>
                <Button onClick={() => submit("accepted")} disabled={sending}><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Accept</Button>
              </div>
              <p className="mt-2 text-[11.5px] leading-4 text-[#8189a0]">
                Always say what went well and give a next step. “Try again” also needs the one thing to improve.
                The parent gets an email when you send.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
